import {useState} from "react";

import {connect} from "react-dynadux";
import {dynaSwitch} from "dyna-switch";

import {
  IWorkzonesAppSettings,
  EWorkZonesManagementRights,
  EWZTXMode,
  EWZClosureSelectionMode,
  getDefaultWorkzoneAppSettings,
  validateWorkzonesAppSettings,
} from "mhc-server/dist/interfaces";

import {IAppStore} from "../../../../state/IAppStore";

import {
  GridContainer,
  GridItem,
} from "mhc-ui-components/dist/Grid";
import {
  FlexContainerVertical,
  FlexContainerResponsive,
  FlexItemMin,
  FlexItemMax,
} from "mhc-ui-components/dist/FlexContainer";
import {
  Alert,
  EAlertType,
} from "mhc-ui-components/dist/Alert";
import {IsLoading} from "mhc-ui-components/dist/IsLoading";
import {ContainerSection} from "mhc-ui-components/dist/ContainerSection";
import {FormToolbar} from "mhc-ui-components/dist/FormToolbar";
import {ErrorBanner} from "mhc-ui-components/dist/ErrorBanner";
import {
  Input,
  EInputType,
} from "mhc-ui-components/dist/Input";
import {InputSelect} from "mhc-ui-components/dist/InputSelect";
import {InputSwitch} from "mhc-ui-components/dist/InputSwitch";
import {PromptRouteChange} from "mhc-ui-components/dist/PromptRouteChange";
import {
  useForm,
  EViewMode,
  EFormType,
} from "mhc-ui-components/dist/useForm";
import {
  convertEnumToValueLabel,
} from "utils-library/dist/utils";

import {OfflineInfo} from "../../components/OfflineInfo/OfflineInfo";

import {
  IOfflineInfo,
  EOfflineStatus,
} from "../../api/interfaces";
import {apiWorkzoneAppSettingsLoad} from "../../api/apiWorkzoneAppSettingsLoad";
import {apiWorkzoneAppSettingsSave} from "../../api/apiWorkzoneAppSettingsSave";

import {ItisCodesEditor} from "./component/ItisCodesEditor";

import {createIcon} from "mhc-ui-components/dist/IconComponent";
import SettingsIcon from "@mui/icons-material/Tune";

export interface IWorkzonesAppSettingsProps {
  store: IAppStore;
}

export const WorkzonesAppSettings = connect((props: IWorkzonesAppSettingsProps): JSX.Element => {
  const {
    store: {
      app: {
        state: {
          online,
          companyId,
        },
      },
      userAuth: {
        state: {user: {id: userId}},
        utils: {userHasAllRights},
      },
      workzonesManagement: {actions: {refreshOfflineItemsCounter}},
    },
  } = props;

  const userCanAccess = userHasAllRights([EWorkZonesManagementRights.WORKZONES_EDIT]);

  const [offlineInfo, setOfflineInfo] = useState<IOfflineInfo>({
    status: EOfflineStatus.ACTUAL_VERSION,
    userMessage: '',
  });

  const useFormApi = useForm<IWorkzonesAppSettings, string>({
    formType: EFormType.VIEW_EDIT,
    loadDataId: '*', // Settings have no Id, we just pass something to start the load.
    emptyFormData: getDefaultWorkzoneAppSettings(),
    userCanEdit: userCanAccess,

    validationRules: validateWorkzonesAppSettings,

    onApiGet: async () => {
      const response = await apiWorkzoneAppSettingsLoad(companyId, userId);
      setOfflineInfo(response.offlineInfo);
      return response.data;
    },
    onApiPut: async (settings) => {
      setOfflineInfo({
        status: EOfflineStatus.ACTUAL_VERSION,
        userMessage: '',
      });
      const response = await apiWorkzoneAppSettingsSave(companyId, userId, settings);
      if (!online) refreshOfflineItemsCounter();
      setOfflineInfo(response.offlineInfo);
    },
  });

  const {
    data: {
      psid,
      serviceChannel,
      txmode,
      intervalInMs,
      priority,
      useSignature,
      useEncryption,
      closureSelectionMode,
      itisCodes,
    },
    viewMode,
    alertViewer,
    confirmViewer,
    isChanged,
    isLoading,
    loadFatalError,
    validationResult: {
      dataValidation: validationErrors,
      customValidation,
    },
    change,
    formProps,
  } = useFormApi;

  const readOnly = viewMode === EViewMode.VIEW;

  if (loadFatalError) {
    return (
      <ErrorBanner
        defaultErrorMessage="Cannot load settings"
        error={loadFatalError}
      />
    );
  }

  return (
    <form
      style={{
        height: '100%',
        maxWidth: 800,
        margin: 'auto',
      }}
      {...formProps}
    >
      <PromptRouteChange
        active={isChanged}
        getPrompt={()=>({
          title: 'You have unsaved changes',
          message: 'Are you sure you want to leave?',
        })}
      />

      <IsLoading
        isLoading={isLoading}
        fullHeight
      >
        <FlexContainerVertical fullHeight>

          <FlexItemMin>
            <FlexContainerResponsive verticalOnMobile>
              <FlexItemMax>
                <ContainerSection
                  h={1}
                  Icon={createIcon.byMuiIcon(SettingsIcon)}
                  title="Settings"
                  subtitle="CIM settings"
                />
              </FlexItemMax>
              <FlexItemMin>
                <FormToolbar useFormApi={useFormApi}/>
              </FlexItemMin>
            </FlexContainerResponsive>

            {confirmViewer}

            <Alert
              type={EAlertType.WARNING}
              show={!isLoading && offlineInfo.status !== EOfflineStatus.ACTUAL_VERSION}
              title="Offline warning"
            >
              <OfflineInfo
                offlineInfo={offlineInfo}
              />
            </Alert>
            {offlineInfo.status === EOfflineStatus.ACTUAL_VERSION && alertViewer}

          </FlexItemMin>

          <FlexItemMax overFlowY>

            <GridContainer spacing={2}>

              <GridItem mobile={12}>
                <Input
                  name="psid"
                  label="PSID"
                  ariaLabel="PSID"
                  required
                  readOnly={readOnly}
                  helperLabel="Decimal"
                  value={psid}
                  validationError={validationErrors.psid}
                />
              </GridItem>

              <GridItem mobile={12} tablet={6}>
                <InputSelect
                  name="serviceChannel"
                  label="Service channel"
                  ariaLabel="Service channel"
                  required
                  readOnly={readOnly}
                  helperLabel="10Mhz spaced channels"
                  options={
                    "172,180,183,SCH"
                      .split(',')
                      .map(v => ({
                        value: v,
                        label: v,
                      }))
                  }
                  value={serviceChannel}
                  validationError={validationErrors.serviceChannel}
                />
              </GridItem>
              <GridItem mobile={12} tablet={6}>
                <InputSelect
                  name="txmode"
                  label="TX mode"
                  ariaLabel="TX mode"
                  required
                  readOnly={readOnly}
                  options={convertEnumToValueLabel(EWZTXMode)}
                  value={txmode.toString()}
                  validationError={validationErrors.txmode}
                />
              </GridItem>

              <GridItem mobile={12} tablet={6}>
                <Input
                  name="intervalInMs"
                  label="Interval"
                  ariaLabel="Interval"
                  required
                  readOnly={readOnly}
                  type={EInputType.NUMBER}
                  helperLabel="In milliseconds. Valid values: 0..5000"
                  value={intervalInMs.toString()}
                  validationError={validationErrors.intervalInMs}
                />
              </GridItem>
              <GridItem mobile={12} tablet={6}>
                <Input
                  name="priority"
                  label="Priority"
                  ariaLabel="Priority"
                  required
                  readOnly={readOnly}
                  type={EInputType.NUMBER}
                  helperLabel="Valid values: 0..63"
                  value={priority.toString()}
                  validationError={validationErrors.priority}
                />
              </GridItem>

              <GridItem mobile={6}>
                <InputSwitch
                  label="Use signature"
                  ariaLabel="Use signature"
                  value={useSignature}
                  readOnly={readOnly}
                  onChange={v => change({useSignature: v})}
                  validationError={validationErrors.useSignature}
                />
              </GridItem>
              <GridItem mobile={6}>
                <InputSwitch
                  label="Use encryption"
                  ariaLabel="Use encryption"
                  value={useEncryption}
                  readOnly={readOnly}
                  onChange={v => change({useEncryption: v})}
                  validationError={validationErrors.useEncryption}
                />
              </GridItem>

              <GridItem mobile={12} tablet={8}>
                <InputSelect
                  name="closureSelectionMode"
                  label="Default Closure Selection Mode"
                  ariaLabel="Default Closure Selection Mode"
                  readOnly={readOnly}
                  options={convertEnumToValueLabel(EWZClosureSelectionMode)}
                  value={closureSelectionMode.toString()}
                  helperLabel={
                    dynaSwitch<string>(
                      closureSelectionMode,
                      '',
                      {
                        [EWZClosureSelectionMode.SIMPLE]: `Select CIM Closure by one Simple "Type of closure" drop down.`,
                        [EWZClosureSelectionMode.CUSTOM]: `Select CIM Closure by Custom "Itis codes", "Closed lane" and "Closed shoulder" drop downs`,
                      },
                    )
                  }
                  validationError={validationErrors.closureSelectionMode}
                />
              </GridItem>


              <GridItem mobile={12}>
                <ItisCodesEditor
                  viewMode={viewMode}
                  itisCodes={itisCodes}
                  validationError={{
                    mainError: validationErrors.itisCodes,
                    removeErrorsByWorkzones: customValidation?.workzonesRemovedItisCodes,
                  }}
                  onChange={itisCodes => change({itisCodes})}
                />
              </GridItem>

            </GridContainer>
          </FlexItemMax>

        </FlexContainerVertical>

      </IsLoading>
    </form>
  );
});
